import { IKgModelCard } from 'app/extreport/concierge/answering/kgModelCardTypes';
import { deriveReviewStatus, deriveRowStatus, RowStatus } from './rowStatus';

export interface BannerInput {
    ownRoute: boolean;
    biTool: 'powerbi' | 'tableau' | 'other';
    source: 'own' | 'other';
    cards: IKgModelCard[];
    pick: { label: string; typeLabel: string } | null;
    legacyOwnActive?: boolean;
}

export interface BannerMessage {
    active: boolean;
    key:
        | 'active_own'
        | 'active_own_legacy'
        | 'active_other'
        | 'active_other_after_own'
        | 'active_other_after_own_legacy'
        | 'inactive_own'
        | 'inactive_own_publish'
        | 'inactive_own_review'
        | 'inactive_other'
        | 'inactive_other_only';
    vars: Record<string, string | number>;
}

function termKeyForBiTool(biTool: 'powerbi' | 'tableau' | 'other'): string {
    return biTool === 'tableau' ? 'term_tableau' : 'term_powerbi';
}

// The furthest step any of the report's models has reached decides what the user is told to do next.
function inactiveOwnKey(cards: IKgModelCard[]): 'inactive_own' | 'inactive_own_publish' | 'inactive_own_review' {
    const statuses = cards.map(deriveReviewStatus);
    if (statuses.includes('ready_to_publish')) return 'inactive_own_publish';
    if (statuses.includes('needs_review')) return 'inactive_own_review';
    return 'inactive_own';
}

export function deriveBanner(input: BannerInput): BannerMessage {
    const publishedCards = input.cards.filter((c) => deriveRowStatus(c) === 'published');
    const termKey = termKeyForBiTool(input.biTool);
    const legacyActive = input.cards.length === 0 && !!input.legacyOwnActive;
    const ownActive = input.ownRoute && (publishedCards.length > 0 || legacyActive);
    const names = publishedCards.map((c) => c.name).join(', ');

    const ownMessage = (): BannerMessage =>
        publishedCards.length > 0
            ? { active: true, key: 'active_own', vars: { termKey, count: publishedCards.length, names } }
            : { active: true, key: 'active_own_legacy', vars: { termKey, count: 1 } };

    if (input.source === 'own') {
        return ownActive
            ? ownMessage()
            : { active: false, key: inactiveOwnKey(input.cards), vars: { termKey, count: 1 } };
    }

    if (input.pick) {
        const pickVars = { typeLabel: input.pick.typeLabel, name: input.pick.label };
        if (ownActive) {
            return publishedCards.length > 0
                ? {
                      active: true,
                      key: 'active_other_after_own',
                      vars: { termKey, count: publishedCards.length, names, ...pickVars },
                  }
                : {
                      active: true,
                      key: 'active_other_after_own_legacy',
                      vars: { termKey, count: 1, ...pickVars },
                  };
        }
        return { active: true, key: 'active_other', vars: pickVars };
    }

    if (input.ownRoute) {
        return { active: false, key: 'inactive_other', vars: { termKey, count: 1 } };
    }

    return { active: false, key: 'inactive_other_only', vars: {} };
}

// Unpublished, the report still answers an administrator or Knowledge Graph manager on a model it has
// in the graph: the banner says so to them instead of only "Not Active".
const PREVIEWABLE: RowStatus[] = ['processing', 'needs_review', 'ready_to_publish'];

export function previewAvailable(msg: BannerMessage, cards: IKgModelCard[], canManageKg: boolean): boolean {
    return (
        canManageKg &&
        !msg.active &&
        msg.key.startsWith('inactive_own') &&
        cards.some((card) => PREVIEWABLE.includes(deriveReviewStatus(card)))
    );
}
